import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { authClient } from "@/lib/auth";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
});

function ResetPasswordPage() {
  const { token, error: tokenError } = Route.useSearch();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const inputClassName = "w-full rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-[14px] text-zinc-100 placeholder-zinc-500 outline-none transition-all duration-200 focus:border-white/[0.12] focus:bg-white/[0.05] focus:ring-1 focus:ring-white/[0.08]";

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError("This password reset link is missing a token.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const result = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (result.error) {
        setError(result.error.message ?? "Failed to reset password.");
        setLoading(false);
        return;
      }

      setSuccess("Your password has been set. You can now sign in with email and password.");
      setPassword("");
      setConfirmPassword("");
    } catch {
      setError("Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const resolvedTokenError = tokenError === "INVALID_TOKEN"
    ? "This password reset link is invalid or expired. Request a new one from the login page."
    : tokenError
      ? `This password reset link failed: ${tokenError}`
      : "";

  return (
    <div className="noise-overlay relative flex min-h-screen items-center justify-center overflow-hidden bg-[#09090b]">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute left-1/2 top-[35%] size-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-zinc-500/[0.03] blur-[150px]" />
      </div>

      <div className="relative w-full max-w-[420px] px-6">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 shadow-2xl shadow-black/40 backdrop-blur-xl">
          <div className="mb-8 flex flex-col items-center">
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-2xl bg-white/[0.06] blur-xl" />
              <div className="relative size-14 overflow-hidden rounded-2xl shadow-lg ring-1 ring-white/[0.08]">
                <img src="/cord-logo.svg" alt="Cord" className="size-full" />
              </div>
            </div>
            <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-white">
              Set your password
            </h1>
            <p className="mt-2 text-center text-[14px] leading-relaxed text-zinc-500">
              Finish setting up email and password access for your Cord account.
            </p>
          </div>

          {resolvedTokenError && (
            <p className="mb-4 text-center text-sm font-medium text-red-400/90">{resolvedTokenError}</p>
          )}

          {success ? (
            <div className="space-y-4">
              <p className="text-center text-sm font-medium text-emerald-300/90">{success}</p>
              <Link
                to="/login"
                search={{ redirect: undefined, mode: undefined, email: undefined }}
                className="block w-full rounded-xl bg-white px-4 py-3 text-center text-[14px] font-semibold text-zinc-950 transition-all duration-200 hover:bg-zinc-200"
              >
                Go to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                className={inputClassName}
              />
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={8}
                className={inputClassName}
              />

              {error && (
                <p className="text-center text-sm font-medium text-red-400/90">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full rounded-xl bg-white px-4 py-3 text-[14px] font-semibold text-zinc-950 transition-all duration-200 hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-zinc-900 active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? "Saving password..." : "Set password"}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-[12px] text-zinc-500">
            Prefer not to use a password? You can also go back and sign in with Google.
          </p>
        </div>
      </div>
    </div>
  );
}
